import { matchPath } from "react-router-dom";
import { ROOM_ROUTES } from "./roomIdFromPath";

/**
 * The room routes a reader may open with nothing but the URL: every piece of
 * room content that has a Share button.
 *
 * The decision (2026-09-24, `docs/shared-content-access/README.md`): all
 * content in a room, publications and resources alike, is shareable by link. A
 * reader with no account, or with no membership, who follows a colleague's link
 * gets the page. The room ROW is what stays gated, and these pages need its
 * colours and nothing else, so the router serves them from the public room card
 * when the row does not load.
 *
 * It lives here rather than in `AppRoutes` because two places have to agree
 * about it: the router, which serves these paths from the public room card, and
 * `useRoomGuard`, whose 403 branch would otherwise redirect a non-member away
 * from them.
 *
 * Quizzes and congress documents are not here. They have no Share button, and
 * their API reads still require membership (`guardRoom`).
 */
const SHAREABLE_ROOM_ROUTES = [
  ROOM_ROUTES.article,
  ROOM_ROUTES.playlist,
  ROOM_ROUTES.video,
  ROOM_ROUTES.videoStory,
  ROOM_ROUTES.infographic,
  ROOM_ROUTES.podcast,
] as const;

export function isRoomContentPath(pathname: string): boolean {
  // The EXACT route shapes, never a prefix of them.
  //
  // A prefix match is wrong twice over. `/room/playlists/:roomId` is the
  // members-only shelf, one character from the shared playlist route. And
  // everything BELOW an article is a different page with different rules:
  // `/room/post/:roomId/:slug/chat` is Chat with Paper, which writes a
  // conversation against the reader's account, so it is not something a link
  // alone should open.
  return SHAREABLE_ROOM_ROUTES.some(
    (path) => matchPath(pathname, { path, exact: true }) !== null
  );
}

/**
 * Where Back goes from a shared piece of content when there is no history to
 * return to (a link opened cold, a notification, an external referrer).
 *
 * A signed-out reader always goes to `/`. The root is the front door for them:
 * the welcome page on mobile, which is the install wall on mobile web, and the
 * sign-in gate on desktop. Before this rule, the same button raised the install
 * sheet again on mobile web, went to `/discovery` outside a room and to `/`
 * inside one, so one reader reached three different places.
 *
 * For a signed-in reader, the room is only a destination for someone who can
 * open it. The row route carries `guardRoomListing`, so that is a member, or
 * anyone at all when the room is public or discoverable. The last case is the
 * reader a colleague's link most often brings: not in the room, but free to
 * join it, and the room page offers them its Join button.
 *
 * Only a private client space refuses its row to a non-member. Sending that
 * reader to `/room/discovery/:roomId` walks them into the members-only shelf and
 * straight back out of it. `/` is the honest answer, because the root resolves
 * to the reader's own home (`getInitialRoute`) rather than to a fixed page.
 */
export type SharedContentBackReader =
  | { readonly kind: "signed-out" }
  | {
      readonly kind: "signed-in";
      readonly roomId: string | null;
      /**
       * Whether the room ROW loaded, which is the only thing the client can
       * observe. It is NOT membership: see above for who reads the row.
       */
      readonly hasRoom: boolean;
    };

export function sharedContentBackDestination(
  reader: SharedContentBackReader
): string {
  switch (reader.kind) {
    case "signed-out":
      return "/";
    case "signed-in":
      if (reader.roomId === null) return "/discovery";
      return reader.hasRoom ? `/room/discovery/${reader.roomId}` : "/";
    default:
      throw new Error(reader satisfies never);
  }
}
